import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { STARTUP_API_URL } from "../apiConfig";
import Navbar from "../components/navbar";
import Footer from "../components/Footer";
import {
  FiUpload, FiFile, FiCheck, FiX, FiRefreshCw,
  FiDownload, FiArrowLeft, FiAlertCircle
} from "react-icons/fi";

interface UploadRecord {
  id: number;
  filename: string;
  status: string;
  total_rows: number;
  processed_rows: number;
  successful_rows: number;
  failed_rows: number;
  uploaded_by: string;
  created_at: string;
  error_message?: string;
  error_details?: { row: number; name: string; errors: string[] }[];
}

const ExcelUpload = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [currentUpload, setCurrentUpload] = useState<UploadRecord | null>(null);
  const [uploads, setUploads] = useState<UploadRecord[]>([]);
  const [showTemplate, setShowTemplate] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    fetchUploads();
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (currentUpload && (currentUpload.status === "pending" || currentUpload.status === "processing")) {
      interval = setInterval(() => {
        checkUploadStatus(currentUpload.id);
      }, 2000);
    }
    return () => clearInterval(interval);
  }, [currentUpload]);

  const fetchUploads = async () => {
    try {
      const response = await axios.get(`${STARTUP_API_URL}/api/v1/excel/uploads`);
      setUploads(response.data.data || []);
    } catch (error) {
      console.error("Error fetching uploads:", error);
    }
  };

  const checkUploadStatus = async (uploadId: number) => {
    try {
      const response = await axios.get(`${STARTUP_API_URL}/api/v1/excel/upload/${uploadId}/status`);
      const data = response.data.data;
      setCurrentUpload(data);

      if (data.status === "completed" || data.status === "failed") {
        fetchUploads();
      }
    } catch (error) {
      console.error("Error checking upload status:", error);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
        alert("Please select an Excel file (.xlsx or .xls)");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
        alert("Please select an Excel file (.xlsx or .xls)");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      setUploadProgress(0);

      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("uploaded_by", localStorage.getItem("uid") || "admin");

      const response = await axios.post(`${STARTUP_API_URL}/api/v1/excel/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const progress = progressEvent.total
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0;
          setUploadProgress(progress);
        },
      });

      const uploadId = response.data.data.upload_id;
      setCurrentUpload({
        id: uploadId,
        filename: selectedFile.name,
        status: "processing",
        total_rows: response.data.data.total_rows,
        processed_rows: 0,
        successful_rows: 0,
        failed_rows: 0,
        uploaded_by: localStorage.getItem("uid") || "admin",
        created_at: new Date().toISOString(),
      });
      setSelectedFile(null);
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Error uploading file. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed": return "text-green-400 bg-green-400/10";
      case "processing": return "text-blue-400 bg-blue-400/10";
      case "pending": return "text-yellow-400 bg-yellow-400/10";
      case "failed": return "text-red-400 bg-red-400/10";
      default: return "text-gray-400 bg-gray-400/10";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed": return <FiCheck />;
      case "processing": return <FiRefreshCw className="animate-spin" />;
      case "pending": return <FiRefreshCw />;
      case "failed": return <FiX />;
      default: return null;
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        background: "radial-gradient(35% 19% at 50% 0%, rgba(255, 84, 31, 0.5) 0%, rgba(0, 0, 0, 0) 100%), black",
        color: "white",
      }}
    >
      {/* Navbar */}
      <div className="w-full bg-transparent border-b border-white/10">
        <Navbar onSearchClick={() => {}} />
      </div>

      {/* Main Content */}
      <div className="flex-1 w-full max-w-4xl mx-auto px-4 pt-24 pb-8">
        {/* Back Button */}
        <button
          onClick={() => navigate("/admin")}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition"
        >
          <FiArrowLeft /> Back to Admin Panel
        </button>

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: "Sora" }}>
              Excel Upload
            </h1>
            <p className="text-gray-400 mt-1">Import startups from Excel files</p>
          </div>
          <button
            onClick={() => setShowTemplate(!showTemplate)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-white/20 hover:bg-white/10 transition"
          >
            <FiDownload /> Template Info
          </button>
        </div>

        {/* Template Info */}
        {showTemplate && (
          <div className="mb-8 p-6 rounded-xl border border-white/10 bg-white/5">
            <h3 className="text-lg font-semibold mb-4">Expected Excel Format</h3>
            <div className="space-y-3">
              <div>
                <p className="text-green-400 font-medium">Required Columns:</p>
                <p className="text-gray-400">name (or company_name, startup_name)</p>
              </div>
              <div>
                <p className="text-blue-400 font-medium">Optional Columns:</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                  {[
                    "description", "website", "founded_year", "employee_count",
                    "country", "state", "city", "category", "sector",
                    "funding_stage", "total_funding", "email", "phone",
                    "linkedin_url", "twitter_url", "incubator", "tags"
                  ].map((col) => (
                    <span key={col} className="text-sm text-gray-400 bg-white/5 px-2 py-1 rounded">
                      {col}
                    </span>
                  ))}
                </div>
              </div>
              <div className="mt-4 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                <p className="text-yellow-400 text-sm">
                  <FiAlertCircle className="inline mr-2" />
                  Funding amounts can be in various formats: $10M, 10 Cr, 1000000
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Upload Area */}
        <div
          className={`mb-8 p-8 rounded-xl border-2 border-dashed transition-colors ${
            dragOver
              ? "border-[#FF541F] bg-[#FF541F]/10"
              : "border-white/20 hover:border-white/40"
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <div className="text-center">
            <FiUpload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p className="text-lg mb-2">
              {selectedFile ? selectedFile.name : "Drag and drop your Excel file here"}
            </p>
            <p className="text-gray-400 mb-4">or</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-6 py-2 rounded-lg border border-white/20 hover:bg-white/10 transition"
            >
              Browse Files
            </button>
          </div>

          {selectedFile && (
            <div className="mt-6 flex items-center justify-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5">
                <FiFile className="text-green-400" />
                <span>{selectedFile.name}</span>
                <span className="text-gray-400">
                  ({(selectedFile.size / 1024).toFixed(1)} KB)
                </span>
              </div>
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="px-6 py-2 rounded-lg bg-[#FF541F] hover:bg-[#e54a1a] disabled:opacity-50 transition flex items-center gap-2"
              >
                {uploading ? (
                  <>
                    <FiRefreshCw className="animate-spin" />
                    Uploading... {uploadProgress}%
                  </>
                ) : (
                  <>
                    <FiUpload /> Upload
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Current Upload Progress */}
        {currentUpload && (currentUpload.status === "processing" || currentUpload.status === "pending") && (
          <div className="mb-8 p-6 rounded-xl border border-white/10 bg-white/5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Processing: {currentUpload.filename}</h3>
              <span className={`px-3 py-1 rounded-full text-sm flex items-center gap-2 ${getStatusColor(currentUpload.status)}`}>
                {getStatusIcon(currentUpload.status)}
                {currentUpload.status}
              </span>
            </div>
            <div className="mb-4">
              <div className="flex justify-between text-sm text-gray-400 mb-2">
                <span>Progress</span>
                <span>
                  {currentUpload.processed_rows} / {currentUpload.total_rows} rows
                </span>
              </div>
              <div className="w-full h-3 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#FF541F] transition-all duration-500"
                  style={{
                    width: `${currentUpload.total_rows ? (currentUpload.processed_rows / currentUpload.total_rows) * 100 : 0}%`,
                  }}
                />
              </div>
            </div>
            <div className="flex gap-6 text-sm">
              <span className="text-green-400">
                <FiCheck className="inline mr-1" />
                {currentUpload.successful_rows} successful
              </span>
              <span className="text-red-400">
                <FiX className="inline mr-1" />
                {currentUpload.failed_rows} failed
              </span>
            </div>
          </div>
        )}

        {/* Completed Upload Result */}
        {currentUpload && currentUpload.status === "completed" && (
          <div className="mb-8 p-6 rounded-xl border border-green-500/20 bg-green-500/5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <FiCheck className="text-green-400 w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Upload Complete!</h3>
                <p className="text-gray-400">{currentUpload.filename}</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 rounded-lg bg-white/5">
                <p className="text-2xl font-bold text-white">{currentUpload.total_rows}</p>
                <p className="text-gray-400 text-sm">Total Rows</p>
              </div>
              <div className="p-4 rounded-lg bg-white/5">
                <p className="text-2xl font-bold text-green-400">{currentUpload.successful_rows}</p>
                <p className="text-gray-400 text-sm">Imported</p>
              </div>
              <div className="p-4 rounded-lg bg-white/5">
                <p className="text-2xl font-bold text-red-400">{currentUpload.failed_rows}</p>
                <p className="text-gray-400 text-sm">Failed</p>
              </div>
            </div>
            {currentUpload.error_details && currentUpload.error_details.length > 0 && (
              <div className="mt-4 p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                <p className="text-red-400 font-medium mb-2">Errors:</p>
                <div className="max-h-40 overflow-y-auto space-y-2">
                  {currentUpload.error_details.slice(0, 10).map((error, idx) => (
                    <div key={idx} className="text-sm">
                      <span className="text-gray-400">Row {error.row}:</span>{" "}
                      <span className="text-red-300">{error.name}</span> -{" "}
                      <span className="text-gray-400">{error.errors.join(", ")}</span>
                    </div>
                  ))}
                  {currentUpload.error_details.length > 10 && (
                    <p className="text-gray-400 text-sm">
                      ...and {currentUpload.error_details.length - 10} more errors
                    </p>
                  )}
                </div>
              </div>
            )}
            <button
              onClick={() => setCurrentUpload(null)}
              className="mt-4 px-4 py-2 rounded-lg border border-white/20 hover:bg-white/10 transition"
            >
              Close
            </button>
          </div>
        )}

        {/* Upload History */}
        <div>
          <h2 className="text-xl font-semibold mb-4">Upload History</h2>
          <div className="space-y-3">
            {uploads.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No uploads yet</p>
            ) : (
              uploads.map((upload) => (
                <div
                  key={upload.id}
                  className="p-4 rounded-xl border border-white/10 bg-white/5 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <FiFile className="text-gray-400 w-5 h-5" />
                    <div>
                      <p className="font-medium">{upload.filename}</p>
                      <p className="text-sm text-gray-400">
                        {new Date(upload.created_at).toLocaleDateString()} •{" "}
                        {upload.total_rows} rows
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <span className="text-green-400">{upload.successful_rows}</span>
                      <span className="text-gray-400"> / </span>
                      <span className="text-red-400">{upload.failed_rows}</span>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm flex items-center gap-2 ${getStatusColor(upload.status)}`}>
                      {getStatusIcon(upload.status)}
                      {upload.status}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ExcelUpload;
